import { observable, action, flow, makeObservable } from "mobx";


import { EAIClient } from "Services/EAIClient";
import {Committee, Student, Course, Mapping, TableParams} from "../commonStore";
import { appStore } from "../";
import * as Sentry from "@sentry/react";


import CONFIG from "Config";

export interface UserPreferences {
    notification_frequency: "immediate" | "daily" | "weekly"
    notification_weekly_summary: boolean
}

interface DefaultResult {
    totalCount: number
}

export interface UsersDataQuery {
    data: User[]
    result: DefaultResult
}

export interface User {
    ruuid: string
    status: string
    nickname?: string
    name: string
    prefix: string
    suffix: string
    first_name: string
    last_name: string
    email: string
    student_count: number
    course_count: number
    committee_count: number
    password?: string
    groups?: string[]
    committees?: Committee[]
    is_superadmin?: boolean
    is_admin?: boolean
    is_key_admin?: boolean
    students?: Student[]
    courses?: Course[]
    verified?: boolean
    school_id?: string
    password_set?: boolean
    is_faculty?: boolean
    is_mentor?: boolean
    auth0_id?: string
    tableau_id?: string
    enflux_created?: string
    enflux_updated?: string
    created_by?: string
    remove_on?: string
}

interface TokenStatus {
    password_set: boolean,
    expired: boolean
}

class UserStore {

    [idx: string]: any;

    @observable currentUserPreferences: UserPreferences;
    @observable isLoadingPreferences = false;

    @observable user?: User;
    @observable keyAdminUser?: User;
    @observable users: User[];
    @observable assignees: User[];
    @observable totalUsers: number;
    @observable userOptions: any;
    @observable userObservations: any;
    @observable resentSuccessfully: string[] = [];
    @observable resendInProgress: string[] = [];
    @observable copyInProgress: string[] = [];
    @observable copiedSuccessfully: string[] = [];
    @observable isUpdatingUserById?: string;
    @observable newMappingsCount?: number;
    @observable removedMappingsCount?: number;
    @observable isFetchingUsers?: boolean = false;
    @observable isFetchingUsersForCommittee = false;
    @observable isUpdatingMappingType?: Mapping;
    @observable passwordResetMessage?: string;
    @observable passwordErrorMessage?: string;
    @observable isUpdatingMappings?: boolean;
    @observable resentInviteLinks: Record<string, string> = {};

    @observable tokenStatus: TokenStatus;
    @observable checkingToken: boolean;

    @observable isUpdating = false;
    @observable errorMessage: boolean | string = false;

    client = new EAIClient();
    lastUsersFetch: number;
    inviteLinks: Map<string, string> = new Map();

    constructor() {
        makeObservable(this);
    }

    @flow
    *fetchUserList (listSize?: number, pageNumber?: number): any {
        this.isFetchingUsers = true;
        try {
            const resp = yield this.client.getUsers(listSize, pageNumber);
            this.users = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isFetchingUsers = false;
        }
    }

    @flow
    *fetchUserListAsync(params?: TableParams): any {
        this.lastUsersFetch = Date.now();
        try {
            const resp = yield this.client.getUsersAsync(params, this.lastUsersFetch);
            if (resp.data.result.timestamp < this.lastUsersFetch) return;
            this.totalUsers = resp.data.result.return_count;
            this.users = resp.data.data;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingUsers = false;
        }
    }

    @flow
    *fetchUsersForCommittee(committeeId: number): any {
        this.isFetchingUsersForCommittee = true;
        try {
            const resp = yield this.client.getUsersForCommittee(committeeId);
            return resp.data.data as User[];
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingUsersForCommittee = false;
        }
    }

    @flow
    *fetchCurrentUserPreferences(): any{
        this.isLoadingPreferences = true;
        try {
            const resp = yield this.client.getCurrentUserPreferences();
            this.currentUserPreferences = resp.data.data.preferences;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isLoadingPreferences = false;
        }
    }

    @flow
    *updateCurrentUserPreferences(preferences: {[idx: string]: any}): any{
        this.isLoadingPreferences = true;
        try {
            const resp = yield this.client.updateCurrentUserPreferences(preferences);
            this.currentUserPreferences = resp.data.data.preferences;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isLoadingPreferences = false;
        }
    }

    @flow
    *createUser (data: any): any {
        try {
            this.isUpdatingUserById = data["email"];
            const resp = yield this.client.createUser(data);
            this.user = resp.data.data;
            // update user list
            this.fetchUserList();
            return this.user;
        } catch(e) {
            if( e.response && e.response.data && e.response.data.result && e.response.data.result.error && e.response.data.result.error === "ExhaustedLicenses") {
                appStore.addError(e, "You have reached the limit of users for your school. Please contact support@enflux.com to add more.");
            } else if( e.response && e.response.data && e.response.data.result && e.response.data.result.error && e.response.data.result.error === "UserExists") {
                appStore.addError(e, e.response.data.result.message);
            } else {
                appStore.addError(e);
            }
        } finally {
            this.isUpdatingUserById = undefined;
        }
    }

    @flow
    *updateUser (data: any): any {
        try {
            this.isUpdatingUserById = data["ruuid"];
            const resp = yield this.client.updateUser(data);
            this.user = resp.data.data;
            // update user list
            this.fetchUserList();
            return this.user;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isUpdatingUserById = undefined;
        }
    }

    @flow
    *fetchUserById (ruuid: string): any {
        try {
            const resp = yield this.client.getUserById(ruuid);
            this.user = resp.data.data;
            return this.user;
        } catch(e) {
            appStore.addError(e);
        }
    }

    fetchKeyAdminUser = flow(function *(this: UserStore) {
        try {
            const resp = yield this.client.fetchKeyAdminUser();
            this.keyAdminUser = resp.data.data;
            return this.keyAdminUser;
        } catch (e) {
            if (e.response.status === 404) {
                return;
            }
            appStore.addError(e);
        }
    });

    @flow
    *deleteUser (ruuid: string): any {
        try {
            yield this.client.deleteUser(ruuid);
            this.user = undefined;
            // update user list
            this.fetchUserList();
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    *resendInviteToUser(user: User): any {
        this.resendInProgress = [...this.resendInProgress, user.ruuid];
        try {
            const response = yield this.client.resendUserInvite(user.ruuid);
            const inviteLink = response.data.invite_link;

            if (inviteLink) {
                this.resentInviteLinks[user.ruuid] = inviteLink;
            }

            this.resentSuccessfully = [...this.resentSuccessfully, user.ruuid];
            setTimeout(() => {
                this.resentSuccessfully = this.resentSuccessfully.filter(id => id !== user.ruuid);
            }, 1000);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.resendInProgress = this.resendInProgress.filter(id => id !== user.ruuid);
        }
    }

    @flow
    *copyInviteLink(user: User): any {
        this.copyInProgress = [...this.copyInProgress, user.ruuid];
        try {
            const inviteLink = yield this.client.generateUserInviteLink(user.ruuid);
            if (!inviteLink) throw new Error("No invite link returned from the server");

            yield navigator.clipboard.writeText(inviteLink);
            this.copiedSuccessfully = [...this.copiedSuccessfully, user.ruuid];

            setTimeout(() => {
                this.copiedSuccessfully = this.copiedSuccessfully.filter(id => id !== user.ruuid);
            }, 2000);
        } catch (e) {
            appStore.addError(e, "Failed to copy invite link. Please try again.");
        } finally {
            this.copyInProgress = this.copyInProgress.filter(id => id !== user.ruuid);
        }
    }

    @flow
    *resetPassword(email: string): any {
        try {
            const resp = yield this.client.resetPassword(email);
            if(resp.data.data.reset){
                this.passwordResetMessage = "We've just sent you an email to reset your password";
            } else {
                this.passwordErrorMessage = "Unable to reset password. Please contact your school administrator.";
            }
        } catch(err) {
            this.passwordErrorMessage = "Unable to reset password. Please contact your school administrator.";
        }
    }

    @flow
    *fetchUserOptions (): any {
        // TODO: use requestStore to avoid several requests
        try {
            const resp = yield this.client.fetchUserOptions();
            this.userOptions = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    *fetchAssigneeList (): any {
        // TODO: use requestStore to avoid several requests
        try {
            const resp = yield this.client.getAssignees();
            this.assignees = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    *checkToken(token: string): any {
        this.checkingToken = true;
        try {
            const resp = yield this.client.checkToken(token);
            this.tokenStatus = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.checkingToken = false;
        }
    }

    @flow
    *setInitialPassword (password: string, token: string): any {
        this.isUpdating = true;

        yield this.client.setInitialPassword(password, token)
            .then(() => {
                Sentry.addBreadcrumb({
                    message: "Set initial password successful",
                    data: {
                        activationToken: token,
                        url: window.location.href,
                    },
                });
                location.replace(CONFIG.paths.base);
                this.errorMessage = false;
                this.isUpdating = false;
            })
            .catch((e: any) => {
                this.errorMessage = e.response.data.result.message;
                Sentry.addBreadcrumb({
                    message: "Set initial password failed",
                    data: {
                        activationToken: token,
                        url: window.location.href,
                    },
                });
                this.isUpdating = false;
            });
    }

    @flow
    *updateUserInList(ruuid: string, mappingType?: "students" | "courses" | "committees") {
        this.isUpdatingUserById = ruuid;
        this.isUpdatingMappingType = mappingType;
        yield this.client.getUsers().then((resp: any) => {
            this.users = resp.data.data;
            this.isUpdatingUserById = undefined;
            this.isUpdatingMappingType = undefined;
            this.user = this.users.slice().find((p) => p.ruuid == ruuid);
        });
    }

    @flow
    *addRemoveFacultyMappings(faculty: User, type: Mapping, adds: Array<string | number>, removes: Array<string | number>, shouldRetry = false) {
        this.isUpdatingMappings = true;
        if( removes && removes.length > 0 ) {
            this.removedMappingsCount = undefined;
        }
        if( adds && adds.length > 0 ) {
            this.newMappingsCount = undefined;
        }

        switch (type) {
            case "students":
                yield this.modifyMenteeMappings( faculty, adds as string[], removes as string[]);
                break;

            case "courses":
                yield this.modifyCourseMappings( faculty, adds as number[], removes as number[]);
                break;

            case "committees":
                yield this.modifyCommitteeMappings(faculty, adds as number[], removes as number[], shouldRetry);
                break;

            default:
                break;
        }
    }

    @action
    modifyMenteeMappings ( faculty: User, adds: string[], removes: string[]) {
        const menteeAdds = adds.map( (menteeId) => {
            return {
                    enflux_student_ruuid: menteeId,
                    mentor_ruuid: faculty.ruuid
                };
        });

        const menteeRemoves = removes.map((menteeId: string) => {
            return {
                enflux_student_ruuid: menteeId,
                mentor_ruuid: faculty.ruuid
            };
        });

        const menteeData = {
            adds: menteeAdds,
            removes: menteeRemoves
        };

        return this.client.addMenteeToMentorMapping(menteeData)
            .then( (data: any) => {
                    this.removedMappingsCount = data.data.data.removes.length;
                    this.newMappingsCount = data.data.data.adds.length;
                    this.updateUserInList(faculty.ruuid, "students");
                    this.isUpdatingMappings = false;
                }, e => {
                this.isUpdatingMappings = false;
                appStore.addError(e);
                }
            );
    }

    @action
    modifyCommitteeMappings (faculty: User, adds: number[], removes: number[], shouldRetry = false) {
        const committeeAdds = adds.map((committeeId) => {
            return {
                committee_id: committeeId
            };
        });

        const committeeRemoves = removes.map((committeeId) => {
            return {
                committee_id: committeeId
            };
        });

        const committeeData = {
            adds: committeeAdds,
            removes: committeeRemoves
        };

        return this.client.addCommitteeToFacultyMapping(faculty.ruuid, committeeData)
            .then( (data: any) => {
                this.removedMappingsCount = data.data.data.removes.length;
                this.newMappingsCount = data.data.data.adds.length;
                this.updateUserInList(faculty.ruuid, "committees");
                this.isUpdatingMappings = false;
            },
            e => {
                if (shouldRetry) {
                    const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
                    wait(3000).then(() => this.modifyCommitteeMappings(faculty, adds, removes, false));
                } else {
                    this.isUpdatingMappings = false;
                    appStore.addError(e);
                }
            }
        );
    }

    @action
    modifyCourseMappings ( faculty: User, adds: number[], removes: number[] ) {
        const courseAdds = adds.map((committeeId) => {
            return {
                    course_id: committeeId,
                    faculty_ruuid: faculty.ruuid
                };
        });
        const courseRemoves = removes.map((id) => {
            return {
                    course_id: id,
                    faculty_ruuid: faculty.ruuid
                };
        });
        const courseData = {
            adds: courseAdds,
            removes: courseRemoves
        };

        return this.client.addCourseToFacultyMapping(courseData)
            .then( (data: any) => {
                this.removedMappingsCount = data.data.data.removes.length;
                this.newMappingsCount = data.data.data.adds.length;
                this.updateUserInList(faculty.ruuid, "courses");
                this.isUpdatingMappings = false;
                },
            e => {
                this.isUpdatingMappings = false;
                appStore.addError(e);
            }
        );
    }

    @action
    resetUserById () {
        this.user = undefined;
    }

    @action
    resetAddedMapCount() {
        this.newMappingsCount = undefined;
    }

    @action
    resetRemovedMapCount() {
        this.removedMappingsCount = undefined;
    }

    @action
    resetPasswordResetMessages() {
        this.passwordResetMessage = undefined;
        this.passwordErrorMessage = undefined;
    }

}

const userStore = new UserStore();

export default userStore;
export { UserStore };

